"use client";

import type { AssistantStaticBubbleVariantProps, UserStaticBubbleVariantProps } from "@ui/components/core/BaseChat";
import * as m from "motion/react-m";
import { useCallback } from "react";
import { AssistantPart, StaticAssistantPart } from "./Assistant/AssistantMessageParts";
import { UserMessagePart } from "./User/UserMessageParts";
import type { JSX } from "react";
import type { ChatMessagePart, ChatPartCompleteProps, ChatScenarioRole } from "./types";

interface MessagePartProps {
  role: ChatScenarioRole;
  part: ChatMessagePart;
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  animate?: boolean;
  onComplete?: ChatPartCompleteProps["onComplete"];
  userMessageVariants: UserStaticBubbleVariantProps;
}

function MessagePart({
  role,
  part,
  assistantMessageVariants,
  animate = false,
  onComplete,
  userMessageVariants,
}: MessagePartProps): JSX.Element | null {
  if (role === "user") {
    return (
      <UserMessagePart
        part={part}
        assistantMessageVariants={assistantMessageVariants}
        userMessageVariants={userMessageVariants}
      />
    );
  }

  if (animate && onComplete) {
    return <AssistantPart part={part} onComplete={onComplete} assistantMessageVariants={assistantMessageVariants} />;
  }

  return <StaticAssistantPart part={part} assistantMessageVariants={assistantMessageVariants} />;
}

interface MessagePartsListProps {
  messageId?: string;
  role: ChatScenarioRole;
  parts: ChatMessagePart[];
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  visiblePartCount?: number;
  animateParts?: boolean;
  onPartComplete?: (partIndex: number) => void;
  userMessageVariants: UserStaticBubbleVariantProps;
}

interface AnimatedMessagePartProps {
  role: ChatScenarioRole;
  part: ChatMessagePart;
  partIndex: number;
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  onComplete: (partIndex: number) => void;
  userMessageVariants: UserStaticBubbleVariantProps;
}

function AnimatedMessagePart({
  role,
  part,
  partIndex,
  assistantMessageVariants,
  onComplete,
  userMessageVariants,
}: AnimatedMessagePartProps): JSX.Element {
  const handleComplete = useCallback(() => {
    onComplete(partIndex);
  }, [onComplete, partIndex]);

  return (
    <m.div
      initial={{ opacity: 0, transform: "translateY(0.375rem)" }}
      animate={{ opacity: 1, transform: "translateY(0rem)" }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
    >
      <MessagePart
        role={role}
        part={part}
        animate
        onComplete={handleComplete}
        assistantMessageVariants={assistantMessageVariants}
        userMessageVariants={userMessageVariants}
      />
    </m.div>
  );
}

export function MessagePartsList({
  messageId,
  role,
  parts,
  assistantMessageVariants,
  visiblePartCount = parts.length,
  animateParts = false,
  onPartComplete,
  userMessageVariants,
}: MessagePartsListProps): JSX.Element {
  const visibleParts = parts.slice(0, visiblePartCount);
  const shouldAnimateParts = animateParts && onPartComplete !== undefined;

  return (
    <div className='tw-flex tw-flex-col tw-gap-2'>
      {visibleParts.map((part, index) => {
        if (shouldAnimateParts) {
          return (
            <AnimatedMessagePart
              key={`${messageId}-${index}`}
              role={role}
              part={part}
              partIndex={index}
              assistantMessageVariants={assistantMessageVariants}
              onComplete={onPartComplete}
              userMessageVariants={userMessageVariants}
            />
          );
        }

        return (
          <div key={`${messageId}-${index}`}>
            <MessagePart
              role={role}
              part={part}
              assistantMessageVariants={assistantMessageVariants}
              userMessageVariants={userMessageVariants}
            />
          </div>
        );
      })}
    </div>
  );
}
